import { useMemo, useState } from 'react';
import { Search, Edit } from 'lucide-react';
import { Button } from '../../../components/ui/button.tsx';
import { Input } from '../../../components/ui/input.tsx';
import { Card, CardContent } from '../../../components/ui/card.tsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog.tsx';
import { Label } from '../../../components/ui/label.tsx';
import {
  RadioGroup,
  RadioGroupItem,
} from '../../../components/ui/radio-group.tsx';
import { Badge } from '../../../components/ui/badge.tsx';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '../../../components/ui/avatar.tsx';
import {
  useGetAllStaff,
  useUpdateStaff,
} from '../../../api/staff/staffQueries.js';
import { LoadingSpinner } from '../../../components/ui/loading-spinner.tsx';
import { useGetAllRoles } from '../../../api/roles/rolesQueries.js';

export default function UsersManagement() {
  const [searchTerm, setSearchTerm] = useState('');
  const [isAssignRolesModalOpen, setIsAssignRolesModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);

  const {
    data: staffData,
    isLoading: isStaffLoading,
    isError: isStaffError,
    refetch: refetchStaff,
  } = useGetAllStaff();

  const {
    data: roles,
    isLoading: isRolesLoading,
    isError: isRolesError,
  } = useGetAllRoles();

  const { mutate: updateStaff } = useUpdateStaff();

  const filteredUsers = useMemo(() => {
    return staffData.filter(
      (user) =>
        user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.lastName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [staffData, searchTerm]);

  const handleAssignRoles = (user) => {
    setSelectedUser(user);
    console.log(user);
    setIsAssignRolesModalOpen(true);
  };

  const handleSaveRoles = () => {
    if (selectedUser) {
      updateStaff([selectedUser.id, { roleId: selectedRole }]);
    }
    setIsAssignRolesModalOpen(false);
    setSelectedUser(null);
    setSelectedRole(null);
  };

  const getUserInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  return (
    <div className="space-y-6 bg-white p-6 rounded-lg">
      <div className="flex items-center gap-x-6">
        <h1 className="text-lg font-semibold text-foreground">
          Users Management
        </h1>
        {/* Search Bar */}
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search Users by Name, Email or Date"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-purple-50 text-muted-foreground focus:ring-purple-500 focus:border-purple-500"
          />
        </div>
      </div>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          <div className="space-y-0">
            {/* Header */}
            <div className="grid grid-cols-12 gap-4 p-4 border-b bg-muted/30 text-sm font-medium text-muted-foreground">
              <div className="col-span-4">USER</div>
              <div className="col-span-3">ROLES</div>
              <div className="col-span-2">STATUS</div>
              <div className="col-span-2">CREATED</div>
              <div className="col-span-1 text-right">ACTIONS</div>
            </div>

            {/* User Rows */}
            {isStaffError ? (
              <div className="p-2 text-center">
                <p>Error loading staff data</p>
                <Button onClick={refetchStaff}>Retry</Button>
              </div>
            ) : (
              <>
                {isStaffLoading ? (
                  <div className="p-3 flex justify-center">
                    <LoadingSpinner />
                  </div>
                ) : (
                  <>
                    {filteredUsers.map((user) => (
                      <div
                        key={user.id}
                        className="grid grid-cols-12 gap-4 p-4 border-b last:border-b-0 hover:bg-muted/20"
                      >
                        <div className="col-span-4 flex items-center space-x-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage
                              src={user.avatar || '/placeholder.svg'}
                              alt={user.name}
                            />
                            <AvatarFallback className="text-xs bg-purple-600/10 text-purple-600">
                              {getUserInitials(user.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium">{user.name}</div>
                            <div className="text-sm text-muted-foreground">
                              {user.email}
                            </div>
                          </div>
                        </div>
                        <div className="col-span-3 flex items-center">
                          <div className="flex flex-wrap gap-1">
                            <Badge
                              key={user.role.id}
                              variant="secondary"
                              className="text-xs bg-purple-600/10 text-purple-600 border-purple-600/20 capitalize"
                            >
                              {user.role.name}
                            </Badge>
                          </div>
                        </div>
                        <div className="col-span-2 flex items-center">
                          <Badge
                            variant={user.verified ? 'default' : 'secondary'}
                            className={
                              user.verified
                                ? 'bg-green-500/10 text-green-600 border-green-500/20'
                                : 'bg-gray-500/10 text-gray-600 border-gray-500/20'
                            }
                          >
                            {user.verified ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                        <div className="col-span-2 flex items-center">
                          <span className="text-sm text-muted-foreground">
                            {user.startDate.split('T')[0]}
                          </span>
                        </div>
                        <div className="col-span-1 flex items-center justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleAssignRoles(user)}
                            className="h-8 w-8 p-0 text-purple-600 hover:text-purple-600 hover:bg-purple-600/10"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Assign Roles Modal */}
      <Dialog
        open={isAssignRolesModalOpen}
        onOpenChange={setIsAssignRolesModalOpen}
      >
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Assign Roles to
              {` ${selectedUser?.firstName} ${selectedUser?.lastName}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>User Information</Label>
                <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                  <Avatar className="h-10 w-10">
                    <AvatarImage
                      src={selectedUser?.photoUrl || '/placeholder.svg'}
                      alt={`${selectedUser?.firstName} ${selectedUser?.lastName}`}
                    />
                    <AvatarFallback className="bg-purple-600/10 text-purple-600">
                      {selectedUser
                        ? getUserInitials(
                            `${selectedUser?.firstName} ${selectedUser?.lastName}`
                          )
                        : ''}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="font-medium">
                      {selectedUser?.firstName} {selectedUser?.lastName}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {selectedUser?.email}
                    </div>
                  </div>
                </div>
              </div>

              {selectedUser?.role && (
                <div className="space-y-2">
                  <Label>Currently Assigned Role</Label>
                  <div className="flex flex-wrap gap-2">
                    <Badge
                      key={selectedUser.role.id}
                      variant="secondary"
                      className="flex items-center gap-1 bg-purple-600/10 text-purple-600 border-purple-600/20 capitalize"
                    >
                      {selectedUser.role.name}
                      <button
                        onClick={() =>
                          // Todo: Chnage function utitlity
                          // handleRoleChange(selectedUser.role.id, false)
                          console.log('Remove role')
                        }
                        className="ml-1 hover:bg-purple-600/20 rounded-full p-0.5"
                      >
                        ×
                      </button>
                    </Badge>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <Label>Available Roles</Label>
                <div className="grid grid-cols-1 gap-3 max-h-60 overflow-y-auto">
                  {isRolesError ? (
                    <div>
                      <p>Error loading roles</p>
                    </div>
                  ) : (
                    <>
                      {isRolesLoading ? (
                        <div>
                          <LoadingSpinner />
                        </div>
                      ) : (
                        <RadioGroup
                          onValueChange={(value) => setSelectedRole(value)}
                        >
                          {roles.data.map((role) => (
                            <div
                              key={role.id}
                              className="flex items-start space-x-3 p-3 border rounded-lg"
                            >
                              <RadioGroupItem value={role.id} id={role.id} />
                              <div className="space-y-1 flex-1">
                                <div className="flex items-center justify-between">
                                  <Label
                                    htmlFor={role.id}
                                    className="font-medium cursor-pointer"
                                  >
                                    {role.name}
                                  </Label>
                                  <Badge
                                    variant={
                                      role.name === role.baseRole
                                        ? 'secondary'
                                        : 'outline'
                                    }
                                    className="text-xs"
                                  >
                                    {role.name === role.baseRole
                                      ? 'System'
                                      : 'Custom'}
                                  </Badge>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {role.permissions.length} permission
                                  {role.permissions.length !== 1 ? 's' : ''}
                                </p>
                              </div>
                            </div>
                          ))}
                        </RadioGroup>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <div className="flex flex-col items-end gap-y-2">
              <div className="flex gap-x-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedRole(null);
                    setSelectedUser(null);
                    setIsAssignRolesModalOpen(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveRoles}
                  className="bg-purple-600 hover:bg-purple-700"
                  disabled={selectedUser?.role.baseRole === 'superadmin'}
                >
                  Save Changes
                </Button>
              </div>
              {selectedUser?.role.baseRole === 'superadmin' && (
                <p className="text-sm text-muted-foreground">
                  You cannot change the role of superadmin. Transfer ownership
                  instead.
                </p>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
